import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ThemeProvider } from './contexts/ThemeContext';
import './App.css';

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-slate-950">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-slate-500 dark:text-slate-400 text-sm">Indlæser...</p>
    </div>
  </div>
);

// Bar Portal - Lazy loaded
const BarLogin = lazy(() => import('./pages/bar/BarLogin'));
const BarHome = lazy(() => import('./pages/bar/BarHome'));
const BarCatalog = lazy(() => import('./pages/bar/BarCatalog'));
const BarCart = lazy(() => import('./pages/bar/BarCart'));
const BarOrders = lazy(() => import('./pages/bar/BarOrders'));
const BarWaste = lazy(() => import('./pages/bar/BarWaste'));
const BarBulletin = lazy(() => import('./pages/bar/BarBulletin'));
const BarTransfer = lazy(() => import('./pages/bar/BarTransfer'));
const BarCount = lazy(() => import('./pages/bar/BarCount'));
const BarShifts = lazy(() => import('./pages/bar/BarShifts'));
const BarIncidents = lazy(() => import('./pages/bar/BarIncidents'));
const BarMap = lazy(() => import('./pages/bar/BarMap'));
const BarIdeas = lazy(() => import('./pages/bar/BarIdeas'));
const BarEquipmentHandout = lazy(() => import('./pages/bar/BarEquipmentHandout'));
const BarResources = lazy(() => import('./pages/bar/BarResources'));
const BarOrderPrint = lazy(() => import('./pages/bar/BarOrderPrint'));

// Admin Portal - Lazy loaded
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminReturnOrders = lazy(() => import('./pages/admin/AdminReturnOrders'));
const AdminInventory = lazy(() => import('./pages/admin/AdminInventory'));
const AdminCategories = lazy(() => import('./pages/admin/AdminCategories'));
const AdminArticles = lazy(() => import('./pages/admin/AdminArticles'));
const AdminBars = lazy(() => import('./pages/admin/AdminBars'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminChat = lazy(() => import('./pages/admin/AdminChat'));
const AdminWaste = lazy(() => import('./pages/admin/AdminWaste'));
const AdminBulletin = lazy(() => import('./pages/admin/AdminBulletin'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports'));
const AdminTransfers = lazy(() => import('./pages/admin/AdminTransfers'));
const AdminManuals = lazy(() => import('./pages/admin/AdminManuals'));
const AdminActivity = lazy(() => import('./pages/admin/AdminActivity'));
const AdminMovements = lazy(() => import('./pages/admin/AdminMovements'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminShifts = lazy(() => import('./pages/admin/AdminShifts'));
const AdminIncidents = lazy(() => import('./pages/admin/AdminIncidents'));
const AdminIdeas = lazy(() => import('./pages/admin/AdminIdeas'));
const AdminScheduledNotifications = lazy(() => import('./pages/admin/AdminScheduledNotifications'));
const AdminBarManualEditor = lazy(() => import('./pages/admin/AdminBarManualEditor'));
const AdminResources = lazy(() => import('./pages/admin/AdminResources'));
const AdminPurchaseOrders = lazy(() => import('./pages/admin/AdminPurchaseOrders'));

// Public pages (no auth)
const BarManualPublic = lazy(() => import('./pages/BarManualPublic'));
const MessePage = lazy(() => import('./pages/MessePage'));
const MesseAdminPage = lazy(() => import('./pages/MesseAdminPage'));
const CompetitionPage = lazy(() => import('./pages/CompetitionPage'));
const CompetitionAdminPage = lazy(() => import('./pages/CompetitionAdminPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const ProjektPage = lazy(() => import('./pages/projekt/ProjektPage'));

function App() {
  return (
    <ThemeProvider>
      <Toaster richColors position="top-center" />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Root redirect */}
            <Route path="/" element={<Navigate to="/bar/login" replace />} />
            
            {/* Public Routes (no auth) */}
            <Route path="/bar-manual" element={<BarManualPublic />} />
            <Route path="/barmanual" element={<BarManualPublic />} />
            
            {/* Bar Portal Routes */}
            <Route path="/bar/login" element={<BarLogin />} />
            <Route path="/bar/home" element={<BarHome />} />
            <Route path="/bar/catalog" element={<BarCatalog />} />
            <Route path="/bar/cart" element={<BarCart />} />
            <Route path="/bar/orders" element={<BarOrders />} />
            <Route path="/bar/waste" element={<BarWaste />} />
            <Route path="/bar/bulletin" element={<BarBulletin />} />
            <Route path="/bar/transfer" element={<BarTransfer />} />
            <Route path="/bar/count" element={<BarCount />} />
            <Route path="/bar/shifts" element={<BarShifts />} />
            <Route path="/bar/incidents" element={<BarIncidents />} />
            <Route path="/bar/map" element={<BarMap />} />
            <Route path="/bar/ideas" element={<BarIdeas />} />
            <Route path="/bar/equipment" element={<BarEquipmentHandout />} />
            <Route path="/bar/resources" element={<BarResources />} />
            <Route path="/bar/order-print" element={<BarOrderPrint />} />
            
            {/* Admin Portal Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/orders" element={<AdminOrders />} />
            <Route path="/admin/return-orders" element={<AdminReturnOrders />} />
            <Route path="/admin/inventory" element={<AdminInventory />} />
            <Route path="/admin/categories" element={<AdminCategories />} />
            <Route path="/admin/articles" element={<AdminArticles />} />
            <Route path="/admin/bars" element={<AdminBars />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/chat" element={<AdminChat />} />
            <Route path="/admin/waste" element={<AdminWaste />} />
            <Route path="/admin/bulletin" element={<AdminBulletin />} />
            <Route path="/admin/reports" element={<AdminReports />} />
            <Route path="/admin/transfers" element={<AdminTransfers />} />
            <Route path="/admin/activity" element={<AdminActivity />} />
            <Route path="/admin/movements" element={<AdminMovements />} />
            <Route path="/admin/settings" element={<AdminSettings />} />
            <Route path="/admin/shifts" element={<AdminShifts />} />
            <Route path="/admin/incidents" element={<AdminIncidents />} />
            <Route path="/admin/ideas" element={<AdminIdeas />} />
            <Route path="/admin/scheduled-notifications" element={<AdminScheduledNotifications />} />
            <Route path="/admin/bar-manual" element={<AdminBarManualEditor />} />
            <Route path="/admin/resources" element={<AdminResources />} />
            <Route path="/admin/purchase-orders" element={<AdminPurchaseOrders />} />
            
            {/* Messe Competition Routes (public) */}
            <Route path="/messe" element={<CompetitionPage />} />
            <Route path="/messe/admin" element={<CompetitionAdminPage />} />
            <Route path="/messe/vilkaar" element={<TermsPage />} />
            <Route path="/messe-old" element={<MessePage />} />
            <Route path="/messe-old/admin" element={<MesseAdminPage />} />
            
            {/* Calendar/Job Management */}
            <Route path="/kalender" element={<CalendarPage />} />
            
            {/* Project Management (public) */}
            <Route path="/projekt" element={<ProjektPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
